/**
 * App — top-level routing for the ASHI Analytics Portal.
 *
 * Public routes:
 *   /login      — sign-in page
 *   /callback   — receives Cognito's OAuth2 redirect
 *
 * Authenticated routes (wrapped in <RequireAuth>):
 *   /            — redirects to /dashboards (the default landing tab —
 *                  changed from /ai because not every user has AI access)
 *   /dashboards  — Tableau dashboard list + viewer
 *   /data        — Data entry forms
 *   /settings    — Config status + logout
 *
 * AI-Analyst-only routes (wrapped in <RequireAIAnalyst>):
 *   /ai          — Claude analyst chat (gated on the ai_analyst Cognito group)
 *
 * Admin-only routes (wrapped in <RequireAdmin>):
 *   /admin/users — Create + list + manage portal users
 */
import { useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import {
  RequireAIAnalyst,
  RequireAdmin,
  RequireAssumptions,
  RequireAuth,
  RequireEventViewer,
} from "./auth/RequireAuth";
import { ChatView } from "./chat/ChatView";
import { fetchConfig } from "./lib/api";
import { Callback } from "./routes/Callback";
import { Dashboards } from "./routes/Dashboards";
import { DataEntry } from "./routes/DataEntry";
import { EventAppointments } from "./routes/events/EventAppointments";
import { EventCompare } from "./routes/events/EventCompare";
import { EventConfig } from "./routes/events/EventConfig";
import { EventRecap } from "./routes/events/EventRecap";
import { EventRecapList } from "./routes/events/EventRecapList";
import { EventRecapNew } from "./routes/events/EventRecapNew";
import { Login } from "./routes/Login";
import { Settings } from "./routes/Settings";
import { UserManagement } from "./routes/UserManagement";
import { Sidebar } from "./ui/Sidebar";

export function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Callback />} />

      {/* Authenticated */}
      <Route element={<RequireAuth />}>
        <Route element={<Layout />}>
          {/* Default landing: /dashboards (every user has access).
              Was /ai but that broke for non-ai_analyst users. */}
          <Route index element={<Navigate to="/dashboards" replace />} />
          <Route path="/dashboards" element={<Dashboards />} />
          <Route path="/settings" element={<Settings />} />

          {/* AI Analyst — gated on ai_analyst group membership */}
          <Route element={<RequireAIAnalyst />}>
            <Route path="/ai" element={<ChatView />} />
          </Route>

          {/* Assumptions Hub — gated on assumptions group membership */}
          <Route element={<RequireAssumptions />}>
            <Route path="/data" element={<DataEntry />} />
          </Route>

          {/* Event Reporting — gated on event_viewer/event_editor; edits re-checked by API + RLS */}
          <Route element={<RequireEventViewer />}>
            <Route path="/events" element={<EventRecapList />} />
            <Route path="/events/new" element={<EventRecapNew />} />
            <Route path="/events/compare" element={<EventCompare />} />
            <Route path="/events/:id" element={<EventRecap />} />
            <Route path="/events/appointments" element={<EventAppointments />} />
            <Route path="/events/config" element={<EventConfig />} />
          </Route>

          {/* Admin-only nested inside the same Layout */}
          <Route element={<RequireAdmin />}>
            <Route path="/admin/users" element={<UserManagement />} />
          </Route>
        </Route>
      </Route>

      {/* Anything else → home */}
      <Route path="*" element={<Navigate to="/dashboards" replace />} />
    </Routes>
  );
}

/**
 * Layout — the sidebar + main content shell. All authenticated routes
 * render their content inside the <Outlet />.
 */
function Layout() {
  const [clientName, setClientName] = useState("ASHI");

  useEffect(() => {
    fetchConfig()
      .then((c) => setClientName(c.client_name))
      .catch(() => {
        // Non-fatal — sidebar will just say "ASHI"
      });
  }, []);

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg)" }}>
      <Sidebar clientName={clientName} />
      <main className="flex-1 h-screen overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
